import { calculateSelectionRect } from 'common/Prose/Plugins/tableSelection/tableSelectionUtils'
import { ResolvedPos } from 'prosemirror-model'
import { EditorState, Selection, TextSelection, Transaction } from 'prosemirror-state'
import { TableMap as PTableMap } from 'prosemirror-tables'
import { type EditorView } from 'prosemirror-view'
import { findTableFromSel, isTableCell } from '.'

import { CellSelection } from './CellSelection'
import { TableMap } from './TableMap'

export enum directionString {
  Up = 'up',
  Down = 'down',
  Left = 'left',
  Right = 'right',
  Forward = 'forward',
  Backward = 'backward',
}

export enum Axis {
  Vertical = 'vert',
  Horizontal = 'horiz',
}

// TODO: Rewrite with our tableMap
export function nextCell($pos: ResolvedPos, axis: Axis, dir: number) {
  let start = $pos.start(-1),
    map = PTableMap.get($pos.node(-1))
  let moved = map.nextCell($pos.pos - start, axis, dir)
  return moved == null ? null : $pos.node(0).resolve(start + moved)
}

function maybeSetSelection(state: EditorState, dispatch: (tx: any) => any, selection: Selection) {
  if (selection.eq(state.selection)) return false
  if (dispatch) {
    dispatch(state.tr.setSelection(selection).scrollIntoView())
  }
  return true
}

export const arrow = (axis: Axis, direction: number) => {
  return (state: EditorState, dispatch: (tx: Transaction) => any, view: EditorView) => {
    let selection = state.selection
    if (selection instanceof CellSelection) {
      return maybeSetSelection(state, dispatch, Selection.near(selection.$headCell, direction))
    }
    if (axis !== Axis.Horizontal && !selection.empty) return false
    let end = atEndOfCell(view, axis, direction)
    if (end === null) return false
    if (axis === Axis.Horizontal) {
      return maybeSetSelection(
        state,
        dispatch,
        Selection.near(state.doc.resolve(selection.head + direction), direction),
      )
    } else {
      let $cell = state.doc.resolve(end),
        $next = nextCell($cell, axis, direction),
        newSelelection
      if ($next) newSelelection = Selection.near($next, 1)
      else if (direction < 0) newSelelection = Selection.near(state.doc.resolve($cell.before(-1)), -1)
      else newSelelection = Selection.near(state.doc.resolve($cell.after(-1)), 1)
      return maybeSetSelection(state, dispatch, newSelelection)
    }
  }
}

export const shiftArrow = (axis: Axis, direction: number) => {
  return (state: EditorState, dispatch: (tx: Transaction) => any, view: EditorView) => {
    let sel = state.selection
    if (!(sel instanceof CellSelection)) {
      let end = atEndOfCell(view, axis, direction)
      if (end == null) return false
      sel = new CellSelection(state.doc.resolve(end), state.doc.resolve(end))
    }
    let $headCell = nextCell((sel as CellSelection).$headCell, axis, direction)
    const $anchorCell = (sel as CellSelection).$anchorCell
    const table = findTableFromSel(sel)
    if (!$headCell || !table) return false
    const tableMap = new TableMap({ posBeforeTable: table.pos, node: table.node })

    const $includedCells = calculateSelectionRect(tableMap, [$anchorCell.pos, $headCell.pos]).map((pos) =>
      view.state.doc.resolve(pos),
    )

    return maybeSetSelection(state, dispatch, new CellSelection($anchorCell, $headCell, $includedCells))
  }
}

export const atEndOfCell = (view: EditorView, axis: Axis, direction: number) => {
  if (!(view.state.selection instanceof TextSelection)) return null
  let { $head } = view.state.selection
  for (let d = $head.depth - 1; d >= 0; d--) {
    let parent = $head.node(d),
      index = direction < 0 ? $head.index(d) : $head.indexAfter(d)
    if (index !== (direction < 0 ? 0 : parent.childCount)) return null
    if (isTableCell(parent)) {
      let cellPosition = $head.before(d)
      // TODO: Fix this the next time the file is edited.
      /* eslint-disable no-nested-ternary */
      let dirString =
        axis === Axis.Vertical
          ? direction > 0
            ? directionString.Down
            : directionString.Up
          : direction > 0
          ? directionString.Right
          : directionString.Left
      /* eslint-enable no-nested-ternary */
      return view.endOfTextblock(dirString as directionString) ? cellPosition : null
    }
  }
  return null
}
